/*
 * Copyright (c) 2026. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

import type { ScenarioHeadlineMetrics } from '@poweralpha/common';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime.js';

dayjs.extend(relativeTime);

/** `2 days ago` */
export const fromNow = (date: string | Date) => dayjs(date).fromNow();

/** A number formatted to three significant figures, split from its unit for styling. */
export interface Figure {
  value: string;
  unit: string;
}

/** Three significant figures: 1.41, 61.4, 912. */
export const formatSignificant = (value: number) => {
  const integerDigits = Math.max(1, Math.floor(Math.log10(Math.abs(value) || 1)) + 1);
  const digits = Math.max(0, 3 - integerDigits);
  return value.toLocaleString('en-US', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
};

const scaleFigure = (value: number, units: ReadonlyArray<string>, prefix = ''): Figure => {
  let scaled = value;
  let unitIndex = 0;
  while (Math.abs(scaled) >= 1000 && unitIndex < units.length - 1) {
    scaled /= 1000;
    unitIndex += 1;
  }
  return { value: `${prefix}${formatSignificant(scaled)}`, unit: units[unitIndex] };
};

export const formatEnergy = (mwh: number) => scaleFigure(mwh, ['MWh', 'GWh', 'TWh']);

export const formatPower = (mw: number) => scaleFigure(mw, ['MW', 'GW']);

/** `$612M`, `$1.48B`: whole-dollar amounts with a magnitude unit. */
export const formatUsd = (usd: number) => scaleFigure(usd, ['', 'k', 'M', 'B'], '$');

/** BOD price: PPA in $/MWh, or the capacity payment for storage-only deals. */
export const formatBodPrice = ({
  ppa_price,
  capacity_price,
}: Pick<ScenarioHeadlineMetrics, 'ppa_price' | 'capacity_price'>): Figure | null => {
  if (ppa_price) return { value: `$${ppa_price.toFixed(2)}`, unit: '/MWh' };
  if (capacity_price) {
    return { value: `$${formatSignificant(capacity_price / 1000)}k`, unit: '/MW-mo' };
  }
  return null;
};
